import React, { useState, useEffect } from 'react';
import { useTranslate } from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Button,
    TextField,
} from '@mui/material';
import {
    Search,
    AttachMoney,
    ElectricBolt,
    Schedule,
    Warning,
    EventSeat,
    Refresh,
} from '@mui/icons-material';
import { FeeStructure, FeeEntityType } from '../../types/fee.types';
import { EntitySelector } from '../fee-schedules/components/EntitySelector';

interface EffectivePricingData {
    entityType: FeeEntityType;
    entityId: string;
    entityName?: string;
    feeStructure: FeeStructure | null;
    scheduleInfo?: {
        id: string;
        name: string;
        source: FeeEntityType;
        templateName?: string;
    };
}

export const EffectivePricingViewer: React.FC = () => {
    const translate = useTranslate();
    
    const [selectedEntity, setSelectedEntity] = useState<{
        type: FeeEntityType;
        id: string;
        name: string;
    }>({ type: 'charger', id: '', name: '' });
    
    const [effectiveDate, setEffectiveDate] = useState<string>(
        new Date().toISOString().slice(0, 16)
    );
    
    const [loading, setLoading] = useState(false);
    const [pricingData, setPricingData] = useState<EffectivePricingData | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleLookup = async () => {
        if (!selectedEntity.id) {
            setError('Please select an entity first');
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const queryParams = new URLSearchParams({
                entityType: selectedEntity.type,
                entityId: selectedEntity.id,
                effectiveDate: new Date(effectiveDate).toISOString(),
            });

            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/backend/fees/structure?${queryParams}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                },
            });

            if (!response.ok) {
                if (response.status === 404) {
                    setPricingData({
                        entityType: selectedEntity.type,
                        entityId: selectedEntity.id,
                        entityName: selectedEntity.name,
                        feeStructure: null,
                    });
                    return;
                }
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            setPricingData({
                entityType: selectedEntity.type,
                entityId: selectedEntity.id,
                entityName: selectedEntity.name,
                feeStructure: data.data,
            });
        } catch (err) {
            console.error('Pricing lookup error:', err);
            setError(err instanceof Error ? err.message : 'Failed to lookup pricing');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number, currency: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'HKD',
        }).format(amount);
    };

    const getComponentIcon = (component: string) => {
        const icons: Record<string, React.ReactNode> = {
            sessionFee: <AttachMoney fontSize="small" />,
            energyFee: <ElectricBolt fontSize="small" />,
            timeFee: <Schedule fontSize="small" />,
            idleFee: <Warning fontSize="small" />,
            reservationFee: <EventSeat fontSize="small" />,
        };
        return icons[component] || <AttachMoney fontSize="small" />;
    };

    const renderFeeComponent = (componentKey: string, component: any, currency: string) => {
        switch (componentKey) {
            case 'sessionFee':
                return (
                    <TableRow key={componentKey}>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getComponentIcon(componentKey)}
                                {translate('feeTemplates.components.sessionFee')}
                            </Box>
                        </TableCell>
                        <TableCell>{formatCurrency(component.amount, currency)}</TableCell>
                        <TableCell>{component.description || '-'}</TableCell>
                    </TableRow>
                );
                
            case 'energyFee':
                return component.rates?.map((rate: any, index: number) => (
                    <TableRow key={`${componentKey}-${index}`}>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getComponentIcon(componentKey)}
                                {rate.name}
                            </Box>
                        </TableCell>
                        <TableCell>{formatCurrency(rate.rate, currency)} per kWh</TableCell>
                        <TableCell>
                            {rate.timeWindows?.length > 0 
                                ? rate.timeWindows.map((tw: any) => 
                                    `${tw.days.join(', ')}: ${tw.start}-${tw.end}`
                                  ).join('; ')
                                : translate('feeTemplates.fields.allTimes')
                            }
                        </TableCell>
                    </TableRow>
                ));
                
            case 'timeFee':
                return component.rates?.map((rate: any, index: number) => (
                    <TableRow key={`${componentKey}-${index}`}>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getComponentIcon(componentKey)}
                                {rate.name || translate('feeTemplates.components.timeFee')}
                            </Box>
                        </TableCell>
                        <TableCell>
                            {formatCurrency(rate.rate, currency)} per {component.unit || 'minute'}
                            {component.freeMinutes > 0 && ` (${component.freeMinutes} free min)`}
                        </TableCell>
                        <TableCell>
                            {rate.timeWindows?.length > 0
                                ? rate.timeWindows.map((tw: any) => `${tw.days.join(', ')}: ${tw.start}-${tw.end}`).join('; ')
                                : translate('feeTemplates.fields.allTimes')
                            }
                        </TableCell>
                    </TableRow>
                ));
                
            case 'idleFee':
                return (
                    <TableRow key={componentKey}>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getComponentIcon(componentKey)}
                                {translate('feeTemplates.components.idleFee')}
                            </Box>
                        </TableCell>
                        <TableCell>
                            {formatCurrency(component.rate, currency)} per minute
                            {component.maxPerSession && ` (max: ${formatCurrency(component.maxPerSession, currency)})`}
                        </TableCell>
                        <TableCell>
                            Grace period: {component.graceMinutes} minutes
                        </TableCell>
                    </TableRow>
                );
                
            case 'reservationFee':
                return (
                    <TableRow key={componentKey}>
                        <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getComponentIcon(componentKey)}
                                {translate('feeTemplates.components.reservationFee')}
                            </Box>
                        </TableCell>
                        <TableCell>{formatCurrency(component.amount, currency)}</TableCell>
                        <TableCell>
                            {component.policyId ?? '-'}
                        </TableCell>
                    </TableRow>
                );
                
            default:
                return null;
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom>
                {translate('feeTools.pricing.title')}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {translate('feeTools.pricing.subtitle')}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3 }}>
                {/* Search Section */}
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {translate('feeTools.pricing.lookup')}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <EntitySelector 
                                source="entity" 
                                showHierarchy={false}
                            />
                            
                            <TextField
                                label={translate('feeTools.pricing.effectiveDate')}
                                type="datetime-local"
                                value={effectiveDate}
                                onChange={(e) => setEffectiveDate(e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                helperText={translate('feeTools.pricing.effectiveDateHelper')}
                            />
                            
                            <Button
                                variant="contained"
                                startIcon={<Search />}
                                onClick={handleLookup}
                                disabled={loading || !selectedEntity.id}
                                fullWidth
                            >
                                {translate('feeTools.pricing.lookupPricing')}
                            </Button>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </CardContent>
                </Card>

                {/* Results Section */}
                <Box>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">
                                    {translate('feeTools.pricing.effectivePricing')}
                                </Typography>
                                {pricingData && (
                                    <Button
                                        startIcon={<Refresh />}
                                        onClick={handleLookup}
                                        disabled={loading}
                                        size="small"
                                    >
                                        {translate('feeTools.pricing.refresh')}
                                    </Button>
                                )}
                            </Box>
                            
                            {loading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                    <CircularProgress />
                                </Box>
                            )}
                            
                            {!loading && !pricingData && (
                                <Alert severity="info">
                                    {translate('feeTools.pricing.noPricingData')}
                                </Alert>
                            )}
                            
                            {pricingData && !pricingData.feeStructure && (
                                <Alert severity="warning">
                                    {translate('feeTools.pricing.noPricingConfigured', {
                                        entity: pricingData.entityName || pricingData.entityId
                                    })}
                                </Alert>
                            )}
                            
                            {pricingData?.feeStructure && (
                                <Box>
                                    {/* Entity Info */}
                                    <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            {pricingData.entityName || pricingData.entityId}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={pricingData.entityType.replace('_', ' ').toUpperCase()}
                                                size="small"
                                                color="primary"
                                            />
                                            <Chip
                                                label={pricingData.feeStructure.currency}
                                                size="small"
                                                variant="outlined"
                                            />
                                            {pricingData.scheduleInfo?.templateName && (
                                                <Chip
                                                    label={`Template: ${pricingData.scheduleInfo.templateName}`}
                                                    size="small"
                                                    color="secondary"
                                                />
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Fee Components */}
                                    <Typography variant="subtitle2" gutterBottom>
                                        {translate('feeTools.pricing.feeComponents')}
                                    </Typography>
                                    
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>{translate('feeTools.pricing.component')}</strong></TableCell>
                                                    <TableCell><strong>{translate('feeTools.pricing.rate')}</strong></TableCell>
                                                    <TableCell><strong>{translate('feeTools.pricing.details')}</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(pricingData.feeStructure.components || {})
                                                    .filter(([_, component]: [string, any]) => component?.enabled)
                                                    .map(([componentKey, component]) => 
                                                        renderFeeComponent(componentKey, component, pricingData.feeStructure!.currency)
                                                    )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {/* Special Dates */}
                                    {pricingData.feeStructure.specialDates && pricingData.feeStructure.specialDates.length > 0 && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                {translate('feeTools.pricing.specialDates')}
                                            </Typography>
                                            <Alert severity="info">
                                                {translate('feeTools.pricing.specialDatesCount', { 
                                                    count: pricingData.feeStructure.specialDates.length 
                                                })}
                                            </Alert>
                                        </Box>
                                    )}

                                    {/* Metadata */}
                                    <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {translate('feeTools.pricing.lastChecked')}: {new Date().toLocaleString()}
                                        </Typography>
                                        <br />
                                        <Typography variant="caption" color="text.secondary">
                                            {translate('feeTools.pricing.version')}: {pricingData.feeStructure.version}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
};